import type { ReactNode } from "react";
import { unitsOf } from "../format";
import { availabilityNote, rowsOf, scenarioLabel, shortHazard, type Selection } from "../model";
import type { Metric, Summary } from "../types";
const Section = ({
  title,
  children
}: {
  title: string;
  children: ReactNode;
}) => <div className="border-0 border-b border-solid border-gray-100 px-4 py-3 last:border-b-0" data-sentry-component="Section" data-sentry-source-file="AboutPanel.tsx">
    <h3 className="m-0 mb-2 text-[10.5px] font-normal uppercase tracking-widest text-gray-500">{title}</h3>
    <div className="text-[13px] leading-relaxed [&_p]:m-0 [&_p]:mb-2">{children}</div>
  </div>;

/** What the metric is, what it could mean for the hazard, where it comes from, and its references. */
export const AboutPanel = ({
  summary,
  selection,
  metric
}: {
  summary: Summary;
  selection: Selection;
  metric: Metric;
}) => {
  const row = rowsOf(summary, selection.hazard).find(r => r.metric_id === metric.id);
  const note = availabilityNote(summary, metric);
  const units = unitsOf(metric);
  const short = shortHazard(selection.hazard);
  // One run for most metrics; where the run differs by scenario, one line each.
  const runs = summary.scenarios.map(s => [scenarioLabel(summary, s.id), metric.source_run?.[s.id]] as const).filter(([, r]) => r);
  const same = new Set(runs.map(([, r]) => r)).size <= 1;
  return <section className="flex min-w-0 flex-col bg-surface lg:row-span-2" data-sentry-component="AboutPanel" data-sentry-source-file="AboutPanel.tsx">
      <Section title="Metric definition" data-sentry-element="Section" data-sentry-source-file="AboutPanel.tsx">
        <p>
          <b>{metric.plain_label}</b>
          {units && <span className="ml-1 font-mono text-gray-500">{units}</span>}
        </p>
        <p>{metric.definition}</p>
        {note && <p className="text-gray-500">{note}</p>}
        {/* The signed-off dashboard's placeholder until each metric's Doc360 page exists. */}
        <p className="text-xs font-medium">Full methodology for this metric (placeholder, link to Doc360)</p>
      </Section>
      <Section title={`What this metric could mean for ${short.charAt(0).toLowerCase()}${short.slice(1)}`} data-sentry-element="Section" data-sentry-source-file="AboutPanel.tsx">
        {row && <p>{row.interpretation}</p>}
      </Section>
      <Section title="Source" data-sentry-element="Section" data-sentry-source-file="AboutPanel.tsx">
        <p>
          <b>{metric.source_label || metric.source}</b>
        </p>
        {same ? runs.length > 0 && <p className="font-mono text-[11px]">{runs[0][1]}</p> : runs.map(([code, run]) => <p key={code} className="font-mono text-[11px]">
                {code}: {run}
              </p>)}
      </Section>
      <Section title="References" data-sentry-element="Section" data-sentry-source-file="AboutPanel.tsx">
        {metric.references?.length ? metric.references.map(ref => <div key={ref} className="break-all">
              {/^https?:\/\//.test(ref) ? <a href={ref} target="_blank" rel="noopener noreferrer">
                  {ref}
                </a> : ref}
            </div>) : <p>—</p>}
      </Section>
    </section>;
};